import React from "react";
import ReactDOM from "react-dom/client";
import "./index.scss";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import ErrorPage from "./pages/error/error-page";
import TransactionDetailsPage from "./pages/transactionDetails/TransactionDetailsPage";
import AddressDetailsPage from "./pages/addressDetails/AddressDetailsPage";
import HomePage from "./pages/home/HomePage";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createTheme, MantineProvider } from "@mantine/core";
import * as Sentry from "@sentry/react";
import GAService from "./services/ga.service";
import { COOKIE_CONNECT_WALLET, SENTRY_PROJECT_ID } from "./constants";
import { CookieService } from "./services/cookie.service";
import ValidatorsPage from "./pages/validators/ValidatorsPage";
import ValidatorDetailsPage from "./pages/validatorDetails/ValidatorDetailsPage";
import ExportImportTransactionDetailsPage from "./pages/exportTransactionDetails/exportImportTransactionDetailsPage";

if (process.env.REACT_APP_DEPLOY_TARGET === "production") {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  console.log = function() {
  }; //Prevent logs on production

  if (CookieService.cookieExists(COOKIE_CONNECT_WALLET)) {
    //Add google analytics if COOKIE_CONNECT_WALLET is found.
    // Otherwise it is initialized after cookie banner is accepted
    GAService.loadGoogleAnalytics();
  }
}
const origin = window.location.origin.includes("localhost") ? "https://flare-space-test.matheo.si" : window.location.origin;
loadWebComponent(origin, "/components/PromoBanner.js");
loadWebComponent(origin, "/components/FlareSpaceMenu.js");

// Dynamically load web components to avoid extensive caching.
function loadWebComponent(origin: string, srcUrl: string) {
  const newScript = document.createElement("script");
  newScript.src = `${origin}${srcUrl}?v=${Date.now()}`;
  document.head.appendChild(newScript);
}


Sentry.init({
  dsn: SENTRY_PROJECT_ID,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 0.01,
  enabled: process.env.NODE_ENV === "production",
});

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter(
  [
    {
      path: "/",
      element: <App />,
      errorElement: <ErrorPage />,
      children: [
        {
          path: "/",
          element: <HomePage />,
        },
        {
          path: "tx/:transactionId",
          element: <TransactionDetailsPage />,
        },
        {
          path: "tx/c-chain/:txId",
          element: <ExportImportTransactionDetailsPage />,
        },
        {
          path: "address/:address",
          element: <AddressDetailsPage />,
        },
        {
          path: "validators",
          element: <ValidatorsPage />,
        },
        {
          path: "validator/:nodeId",
          element: <ValidatorDetailsPage />,
        },
        {
          path: "*",
          element: <ErrorPage />,
        },
      ],
    },
  ],
  {
    basename: document.querySelector("base")?.getAttribute("href") ?? "/",
  },
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: 1000,
      refetchOnWindowFocus: false, // default: true
    },
  },
});

const theme = createTheme({
  cursorType: "pointer",
  fontFamily: "IBM Plex Sans, Helvetica Neue, Arial, sans-serif",
  colors: {
    brand: [
      "#ffeee7",
      "#ffdcd0",
      "#fab7a0",
      "#f78f6d",
      "#f36e41",
      "#f35925",
      "#f24e16",
      "#d83f0b",
      "#c13607",
      "#a92b00",
    ],
  },
  primaryColor: "brand",
  headings: {
    fontFamily: "IBM Plex Sans, Helvetica Neue, Arial, sans-serif",
    fontWeight: "700",
  },
  fontSizes: {
    xs: "12",
    sm: "14",
    md: "16",
    lg: "20",
    xl: "32",
  },
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <MantineProvider withCssVariables={true} theme={theme}>
        <RouterProvider router={router} />
      </MantineProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
